import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Copy, Check, Gift, Share2, ArrowRight } from "lucide-react";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";

/**
 * Friend & Family Referral — public page for anyone (signed-in or not) to
 * generate their referral UID. Canonical Spec v1.2 §2.
 *
 * Route: /refer (public)
 */
export default function ReferPage() {
  const [pricing, setPricing] = useState(null);
  const [form, setForm] = useState({
    referrer_name: "",
    referrer_email: "",
    option: "A",  // default per spec §2.2
  });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/pricing/public").then(({ data }) => setPricing(data)).catch(() => {});
  }, []);

  const money = (c) => `$${((c || 0) / 100).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.referrer_name || !form.referrer_email) {
      setErr("Please enter your name and email.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/refer/create", form);
      setResult(data);
    } catch (e2) {
      setErr(e2.response?.data?.detail || e2.message);
    } finally {
      setSubmitting(false);
    }
  };

  const copy = async () => {
    if (!result?.uid) return;
    try { await navigator.clipboard.writeText(result.uid); }
    catch (err) {
      try {
        const ta = document.createElement("textarea");
        ta.value = result.uid; ta.style.position="absolute"; ta.style.left="-9999px";
        document.body.appendChild(ta); ta.select(); document.execCommand("copy");
        document.body.removeChild(ta);
      } catch (fallbackErr) {
        console.warn("[ReferPage] Clipboard copy failed:", fallbackErr);
      }
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };

  const optionADiscount = money(pricing?.ff_option_a_referee_discount_cents || 10000);
  const optionBReward = money(pricing?.ff_option_b_referrer_cents || 2500);
  const optionBDiscount = money(pricing?.ff_option_b_referee_discount_cents || 7500);

  if (result) {
    const shareMsg =
      `I'm using DS-260USA.com to prepare my family's DS-260 consular cases. ` +
      `Enter my referral UID ${result.uid} at checkout and you'll get a discount. ` +
      `Start here: https://ds-260usa.com`;
    return (
      <AppShell>
        <div className="mx-auto max-w-2xl px-6 py-14 text-center" data-testid="refer-thankyou">
          <div className="mx-auto w-14 h-14 rounded-full flex items-center justify-center mb-4"
               style={{ background: "var(--ai-bg)", color: "var(--ai-text)" }}>
            <CheckCircle2 className="w-7 h-7" />
          </div>
          <h1 className="font-display text-3xl font-extrabold mb-3">Here's your referral UID</h1>
          <div className="my-6">
            <code data-testid="refer-uid" className="text-3xl font-mono font-extrabold px-4 py-2 rounded-lg"
                  style={{ background: "var(--ai-bg)", color: "var(--accent-copper)" }}>
              {result.uid}
            </code>
          </div>
          <div className="flex items-center justify-center gap-2 mb-6">
            <button className="btn-primary text-sm inline-flex items-center gap-1"
                    onClick={copy} data-testid="refer-copy-btn">
              {copied ? <><Check className="w-4 h-4" /> Copied</> : <><Copy className="w-4 h-4" /> Copy UID</>}
            </button>
          </div>
          <div className="card-surface p-4 text-left mb-4">
            <div className="text-xs font-semibold uppercase mb-1" style={{ color: "var(--text-secondary)" }}>
              Suggested share message
            </div>
            <p className="text-sm">{shareMsg}</p>
          </div>
          <p className="text-xs" style={{ color: "var(--text-secondary)" }}>
            Ask your friend to enter <strong>{result.uid}</strong> at checkout on ds-260usa.com.
            They'll get the discount automatically once their case is completed and accepted.
          </p>
          <div className="mt-8">
            <Link to="/" className="btn-ghost text-sm">Back to home</Link>
          </div>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="mx-auto max-w-3xl px-6 py-10" data-testid="refer-page">
        <div className="mb-8">
          <div className="pill pill-ai inline-flex mb-3">
            <Gift className="w-3.5 h-3.5 mr-1.5" /> Friend &amp; Family Referral
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold" style={{ color: "var(--text-primary)" }}>
            Refer a friend. <span style={{ color: "var(--accent-copper)" }}>They save. Or you split it.</span>
          </h1>
          <p className="mt-4 text-[16px] max-w-2xl" style={{ color: "var(--text-secondary)" }}>
            Get your unique referral UID in 30 seconds. Share it with anyone starting a
            family's DS-260 consular case. You choose whether the reward goes entirely to
            your friend (recommended) or you split it with them.
          </p>
        </div>

        <form className="card-surface p-6 space-y-4" onSubmit={submit} data-testid="refer-form">
          <div>
            <label className="field-label">Your name</label>
            <input className="field-input" value={form.referrer_name}
                   onChange={(e) => setForm({ ...form, referrer_name: e.target.value })}
                   placeholder="Your full name"
                   data-testid="refer-input-name" required />
          </div>
          <div>
            <label className="field-label">Your email</label>
            <input type="email" className="field-input" value={form.referrer_email}
                   onChange={(e) => setForm({ ...form, referrer_email: e.target.value })}
                   placeholder="you@example.com"
                   data-testid="refer-input-email" required />
            <p className="text-xs mt-1" style={{ color: "var(--text-secondary)" }}>
              We'll email you your UID so you can find it later.
            </p>
          </div>

          <div>
            <label className="field-label">Choose your reward option</label>
            <div className="grid md:grid-cols-2 gap-3">
              <OptionCard
                selected={form.option === "A"}
                onSelect={() => setForm({ ...form, option: "A" })}
                label="Option A — Gift my reward"
                sub="Recommended · Default"
                referrer="You: $0"
                referee={`Friend: ${optionADiscount} off`}
                blurb="You gift 100% of the reward to your friend. Simpler for everyone — no payout for us to send you."
                testId="refer-option-a"
              />
              <OptionCard
                selected={form.option === "B"}
                onSelect={() => setForm({ ...form, option: "B" })}
                label="Option B — Split the reward"
                sub="Zelle / ACH"
                referrer={`You: ${optionBReward}`}
                referee={`Friend: ${optionBDiscount} off`}
                blurb="You get a payout after your friend's case is accepted as complete. Paid via Zelle or ACH."
                testId="refer-option-b"
              />
            </div>
          </div>

          {err && (
            <div className="text-xs p-3 rounded-md" style={{ background: "#FEF2F2", color: "#991B1B" }}
                 data-testid="refer-error">{err}</div>
          )}

          <div className="pt-2">
            <button type="submit" disabled={submitting} className="btn-primary"
                    data-testid="refer-submit">
              {submitting ? "Generating…" : "Get my referral UID"}
              <ArrowRight className="w-4 h-4 inline ml-1" />
            </button>
            <p className="text-xs mt-2" style={{ color: "var(--text-secondary)" }}>
              This is the Friend &amp; Family program (occasional referrals). If you're a
              professional promoter (student group, community org, influencer),{" "}
              <Link to="/affiliate" className="underline" data-testid="refer-affiliate-link">
                apply to the Affiliate Program
              </Link>{" "}
              instead — you can earn $200 per Qualified Prime DS-260 Case.
            </p>
          </div>
        </form>
      </div>
    </AppShell>
  );
}

function OptionCard({ selected, onSelect, label, sub, referrer, referee, blurb, testId }) {
  return (
    <button type="button" onClick={onSelect} data-testid={testId}
            className="text-left p-4 rounded-lg border-2 transition-colors"
            style={{
              borderColor: selected ? "var(--accent-copper)" : "var(--border-stone)",
              background: selected ? "var(--ai-bg)" : "var(--bg-cream)",
            }}>
      <div className="flex items-center justify-between mb-1">
        <div className="font-display font-bold text-sm">{label}</div>
        {selected && <Check className="w-4 h-4" style={{ color: "var(--accent-copper)" }} />}
      </div>
      <div className="text-xs mb-2" style={{ color: "var(--text-secondary)" }}>{sub}</div>
      <div className="flex gap-2 flex-wrap mb-2">
        <span className="text-[11px] px-2 py-0.5 rounded-full"
              style={{ background: "#F0F0F0", color: "#374151" }}>{referrer}</span>
        <span className="text-[11px] px-2 py-0.5 rounded-full font-semibold"
              style={{ background: "#D1FAE5", color: "#065F46" }}>{referee}</span>
      </div>
      <div className="text-xs" style={{ color: "var(--text-primary)" }}>{blurb}</div>
    </button>
  );
}
